import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import WidgetCard from './WidgetCard';
import { bulkUpdateWidgetPositions } from '../../services/api';
import { Link2 } from 'lucide-react';

function SafeDashboardGrid({ dashboard, onWidgetDeleted, onWidgetUpdated }) {
  const [isLinked, setIsLinked] = useState(false);
  const [crosshairDate, setCrosshairDate] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [localDashboard, setLocalDashboard] = useState(dashboard);
  const widgetRefs = useRef({});
  const debounceTimer = useRef(null);

  useEffect(() => {
    // Delay mounting to ensure DOM is ready
    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Update local dashboard when prop changes
  useEffect(() => {
    setLocalDashboard(dashboard);
  }, [dashboard]);

  // Debounced crosshair update to reduce re-renders
  // Only update crosshair state every 50ms instead of on every mouse pixel
  const debouncedSetCrosshairDate = useCallback((date) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setCrosshairDate(date);
    }, 50); // 50ms debounce - smooth but performant
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Debug logging
  useEffect(() => {
    console.log('SafeDashboardGrid - Dashboard:', localDashboard);
    console.log('SafeDashboardGrid - Widgets:', localDashboard?.widgets);
  }, [localDashboard]);

  // Handle widget update locally (must be before early returns)
  const handleWidgetUpdate = useCallback((updatedWidget) => {
    console.log('SafeDashboardGrid - Widget updated:', updatedWidget);
    const updatedWidgets = localDashboard.widgets.map(w =>
      w.id === updatedWidget.id ? updatedWidget : w
    );
    setLocalDashboard({ ...localDashboard, widgets: updatedWidgets });

    // Notify parent if callback provided
    if (onWidgetUpdated) {
      onWidgetUpdated(updatedWidget);
    }
  }, [localDashboard, onWidgetUpdated]);

  // Ensure widgets is always an array (must be before early returns)
  const widgets = Array.isArray(localDashboard?.widgets) ? localDashboard.widgets : [];

  // Filter out invalid widgets (must be before early returns)
  const validWidgets = useMemo(() => {
    return widgets.filter(w => {
      const isValid = w && w.id && (typeof w.id === 'number' || typeof w.id === 'string');
      if (!isValid) {
        console.warn('SafeDashboardGrid - Invalid widget:', w);
      }
      return isValid;
    });
  }, [widgets]);

  // Create layout with proper defaults (must be before early returns)
  const layout = useMemo(() => {
    return validWidgets.map((widget, index) => ({
      i: String(widget.id),
      x: widget.grid_x ?? 0,
      y: widget.grid_y ?? (index * 4),
      w: widget.grid_w ?? 6,
      h: widget.grid_h ?? 4,
      minW: 3,
      minH: 2,
    }));
  }, [validWidgets]);

  // Handle layout changes
  const handleLayoutChange = useCallback(async (newLayout) => {
    if (!newLayout || newLayout.length === 0) return;

    const positions = newLayout.map(item => ({
      id: parseInt(item.i),
      grid_x: item.x,
      grid_y: item.y,
      grid_w: item.w,
      grid_h: item.h,
    }));

    try {
      await bulkUpdateWidgetPositions(positions);
      console.log('SafeDashboardGrid - Positions updated');
    } catch (error) {
      console.error('SafeDashboardGrid - Error updating positions:', error);
    }
  }, []);

  // Memoize widget components to prevent recreation on every render
  // Must be defined before any early returns
  const memoizedWidgets = useMemo(() => {
    return validWidgets.map(widget => {
      const key = String(widget.id);

      return (
        <div key={key}>
          <WidgetCard
            widget={widget}
            onDelete={onWidgetDeleted}
            onUpdate={handleWidgetUpdate}
            isLinked={isLinked}
            crosshairDate={crosshairDate}
            onCrosshairMove={debouncedSetCrosshairDate}
          />
        </div>
      );
    });
  }, [validWidgets, isLinked, crosshairDate, onWidgetDeleted, handleWidgetUpdate, debouncedSetCrosshairDate]);

  // Early returns AFTER all hooks
  console.log(`SafeDashboardGrid - Valid widgets: ${validWidgets.length} out of ${widgets.length}`);

  if (!localDashboard) {
    return (
      <div className="bg-background border border-muted rounded p-8 text-center">
        <p className="text-text/60">Loading dashboard...</p>
      </div>
    );
  }

  if (validWidgets.length === 0) {
    return (
      <div className="bg-background border border-muted rounded p-8 text-center">
        <p className="text-text/60 mb-2">No widgets yet</p>
        <p className="text-text/40 text-sm">Click "+ Add Widget" to get started</p>
      </div>
    );
  }

  // Store widget components in refs to avoid React children issues
  validWidgets.forEach(widget => {
    const key = String(widget.id);

    if (!widgetRefs.current[key]) {
      widgetRefs.current[key] = (
        <div key={key} className="dashboard-widget-wrapper">
          <WidgetCard
            widget={widget}
            onDelete={onWidgetDeleted}
            onUpdate={handleWidgetUpdate}
            isLinked={isLinked}
            crosshairDate={crosshairDate}
            onCrosshairMove={debouncedSetCrosshairDate}
          />
        </div>
      );
    }
  });

  // Clean up refs for removed widgets
  Object.keys(widgetRefs.current).forEach(key => {
    if (!validWidgets.find(w => String(w.id) === key)) {
      delete widgetRefs.current[key];
    }
  });

  if (!mounted) {
    return (
      <div className="bg-background border border-muted rounded p-8 text-center">
        <p className="text-text/60">Initializing dashboard layout...</p>
      </div>
    );
  }

  // Try rendering without GridLayout first to isolate the issue
  const useSimpleGrid = false; // Enable react-grid-layout for proper height handling

  if (useSimpleGrid) {
    return (
      <div className="bg-background border border-muted rounded p-4" style={{ minHeight: '600px' }}>
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-muted">
          <input
            type="checkbox"
            id="link-charts"
            checked={isLinked}
            onChange={(e) => setIsLinked(e.target.checked)}
            className="w-4 h-4 cursor-pointer"
          />
          <label htmlFor="link-charts" className="text-text text-sm cursor-pointer select-none flex items-center gap-1.5">
            <Link2 className="w-4 h-4" /> Link All Charts (sync time range and crosshair)
          </label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {validWidgets.map(widget => {
            return (
              <div key={widget.id} className="h-96">
                <WidgetCard
                  widget={widget}
                  onDelete={onWidgetDeleted}
                  onUpdate={handleWidgetUpdate}
                  isLinked={isLinked}
                  crosshairDate={crosshairDate}
                  onCrosshairMove={debouncedSetCrosshairDate}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Try a more controlled approach with GridLayout
  return (
    <div className="bg-background border border-muted rounded p-4" style={{ minHeight: '600px' }}>
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-muted">
        <input
          type="checkbox"
          id="link-charts"
          checked={isLinked}
          onChange={(e) => setIsLinked(e.target.checked)}
          className="w-4 h-4 cursor-pointer"
        />
        <label htmlFor="link-charts" className="text-text text-sm cursor-pointer select-none flex items-center gap-1.5">
          <Link2 className="w-4 h-4" /> Link All Charts (sync time range and crosshair)
        </label>
      </div>

      <GridLayout
        className="layout"
        layout={layout}
        cols={12}
        rowHeight={80}
        width={1100}
        onLayoutChange={handleLayoutChange}
        draggableHandle=".drag-handle"
        isDraggable={true}
        isResizable={true}
        compactType="vertical"
        preventCollision={true}
      >
        {memoizedWidgets}
      </GridLayout>
    </div>
  );
}

export default SafeDashboardGrid;